// Plain text of a generated note, for the clipboard and EMR fields, which do
// not render Markdown. Used by copyNoteToClipboard (pages/Visit/components/
// copyNote.ts) for every document in a visit: notes, letters, summaries.
//
// The Chrome extension has the same function in
// apps/chrome-extension/src/lib/plain-note.ts. Keep the two identical below
// this comment: apps/chrome-extension/test/plain-note.test.mjs checks that the
// code matches and runs the cases in lib/__tests__/plainTextCases.ts on both.

// Removes Markdown syntax only, and keeps every other character. "BP > 140",
// "#2", "q4-6h", "5-10 mg", "SpO2 ≥ 94%", "x * y", "snake_case", "[ ]", "|",
// "+", "~" and the like reach the EMR as written.
//
// - Emphasis (**bold**, __bold__, *italic*, _italic_) only when the markers
//   wrap a word or phrase. A "*" or "_" with a space beside it, or inside a
//   word, is text.
// - "#" only as a heading at the start of a line, followed by a space.
// - ">" only from a real quote: two or more lines in a row that each start
//   with "> " and a letter. A single "> 2 weeks of cough", "> 140", ">= 90"
//   or "> Two weeks" line keeps its ">", which may be a comparison.
// - [text](url) becomes text, ![alt](url) becomes alt, <https://…> the URL.
// - Code ticks and ``` fences go, the code stays as written.
// - Horizontal rules (---, ***, ___) and === heading underlines go.
// - Backslash escapes (\*) keep the character only.
// - Lists stay as typed: "1.", "-", "*", "+" and "•" markers, checkboxes and
//   indentation. Line breaks and blank lines between sections stay.
// - Spaces the Markdown writer adds go: trailing spaces and tabs at the end of
//   a line (a "  " hard break) and the extra spaces after a list marker
//   ("1.  Item" becomes "1. Item"). A tab after a marker and spaces inside a
//   line stay.
export const stripMarkdown = (md: string): string => {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];
  let fence: string | null = null;
  let droppedRule = false;
  const quoteLine = (line: string | undefined): boolean =>
    line !== undefined && /^ {0,3}> +\p{L}/u.test(line);
  for (const [index, line] of lines.entries()) {
    const fenceMatch = /^ {0,3}(`{3,}|~{3,})/.exec(line);
    if (fence !== null) {
      if (fenceMatch?.[1]?.startsWith(fence) && /^\s*[`~]+\s*$/.test(line)) {
        fence = null;
      } else {
        out.push(line);
      }
      continue;
    }
    if (fenceMatch?.[1]) {
      fence = fenceMatch[1];
      continue;
    }
    const previous = out[out.length - 1];
    const isRule =
      /^ {0,3}(?:(?:\*[ \t]*){3,}|(?:-[ \t]*){3,}|(?:_[ \t]*){3,})$/.test(line);
    const isUnderline =
      /^ {0,3}=+[ \t]*$/.test(line) &&
      previous !== undefined &&
      previous.trim() !== "";
    if (isRule || isUnderline) {
      droppedRule = true;
      continue;
    }
    // A rule between two blank lines leaves one blank line, not two.
    if (droppedRule && line.trim() === "" && previous?.trim() === "") {
      droppedRule = false;
      continue;
    }
    droppedRule = false;
    let text = line;
    // Quote: only when the line before or after is also a "> " quote line.
    if (
      quoteLine(line) &&
      (quoteLine(lines[index - 1]) || quoteLine(lines[index + 1]))
    ) {
      text = text.replace(/^ {0,3}> +/, "");
    }
    // ATX heading: "## Subjective". "#2" and "#1 Hypertension" are not.
    const heading = /^[ \t]{0,3}#{1,6}(?:[ \t]+|$)/.exec(text);
    if (heading) text = text.slice(heading[0].length);
    // One space after a list marker: "1.  Item", "-   Item".
    text = text.replace(/^([ \t]*(?:\d+[.)]|[-*+•])) {2,}(?=\S)/u, "$1 ");
    out.push(stripInline(text));
  }
  return out
    .join("\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/^(?:[ \t]*\n)+/, "")
    .trimEnd();
};

// Inline Markdown within one line.
const stripInline = (line: string): string => {
  // Code spans and escaped characters are kept as written: they are set aside
  // while the emphasis and link rules run, then put back.
  const kept: string[] = [];
  const keep = (value: string): string => {
    kept.push(value);
    return `${kept.length - 1}`;
  };
  let text = line
    .replace(/(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)/g, (_, _ticks, code: string) =>
      keep(code),
    )
    .replace(/\\([!-/:-@[-`{-~])/g, (_, char: string) => keep(char))
    .replace(/<br\s*\/?>/gi, "\n")
    // Autolinks: <https://example.com> becomes the URL.
    .replace(/<((?:https?|mailto):[^\s<>]+)>/gi, "$1")
    // Images and links: keep the alt text / link text.
    .replace(/!\[([^\]\n]*)\]\([^()\s]*(?:\s+"[^"\n]*")?\)/g, "$1")
    .replace(/\[([^\]\n]+)\]\([^()\s]+(?:\s+"[^"\n]*")?\)/g, "$1");
  // Emphasis. The text inside must not start or end with a space or another
  // marker, so "2 * 3 * 4", "**" or "____" stay. Single "*" and any "_" must
  // not sit inside a word ("2*3*4", "snake_case_name").
  text = text
    .replace(/\*\*\*([^\s*](?:.*?[^\s*])?)\*\*\*/g, "$1")
    .replace(/\*\*([^\s*](?:.*?[^\s*])?)\*\*/g, "$1")
    .replace(
      /(^|[^\p{L}\p{N}_])__([^\s_](?:.*?[^\s_])?)__(?![\p{L}\p{N}_])/gu,
      "$1$2",
    )
    .replace(
      /(^|[^\p{L}\p{N}*])\*([^\s*](?:.*?[^\s*])?)\*(?![\p{L}\p{N}*])/gu,
      "$1$2",
    )
    .replace(
      /(^|[^\p{L}\p{N}_])_([^\s_](?:.*?[^\s_])?)_(?![\p{L}\p{N}_])/gu,
      "$1$2",
    );
  return text.replace(
    /(\d+)/g,
    (_, index: string) => kept[Number(index)] ?? "",
  );
};
